import type { FC, PropsWithChildren } from 'react';
import { createContext } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import i18next from 'i18next';

import { DEFAULT_VALUES, ENTITY_BLOCKS_CONFIG } from 'app/config/urlParams.config';
import {
  isBlockForEntityType,
  isEntity,
  isPeriod,
  isScoreboardType,
  isTab
} from 'app/config/urlParams.config.helper';
import { isFeatureEnable } from 'app/config/features.config.helper';
import { logError } from 'app/utils/errors';
import { setSentryScope } from 'app/utils/sentry';

interface ConfigContextState extends DefaultParams {
  customer: string | undefined;
}

const defaultParams: ConfigContextState = {
  entity: null,
  lang: DEFAULT_VALUES.lang,
  block: DEFAULT_VALUES.block,
  tab: DEFAULT_VALUES.tab,
  id: undefined,
  widgetId: undefined,
  user: undefined,
  period: DEFAULT_VALUES.period,
  period_edit: DEFAULT_VALUES.period_edit,
  full_height: DEFAULT_VALUES.full_height,
  multi_lang: DEFAULT_VALUES.multi_lang,
  title: DEFAULT_VALUES.title,
  origin: undefined,
  parents: undefined,
  jwt: undefined,
  scoreboard: DEFAULT_VALUES.scoreboard,
  customer: undefined
};

const ConfigContext = createContext<ConfigContextState>(defaultParams);

const ConfigContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { entityType = '', customer = '' } = useParams();
  const [searchParams, _] = useSearchParams();

  const entity = isEntity(entityType) ? entityType : null;

  const filterIsBlock = (blockSlug: string): blockSlug is Block =>
    isBlockForEntityType(entity, blockSlug);

  const configContextState = [...searchParams.entries()].reduce((params, param) => {
    const key = param[0] as keyof UrlParams;
    const value = param[1];

    if (value === '') {
      return params;
    }

    switch (key) {
      case 'block': {
        // Filter only allowed blocks
        const blocksParams = value.split(',');
        const validBlocks = blocksParams.filter(filterIsBlock);
        const invalidBlocks = blocksParams.filter(block => !filterIsBlock(block));

        if (invalidBlocks.length) {
          logError('Invalid block parameter', invalidBlocks.join(', '));
        }

        if (validBlocks) {
          if (isFeatureEnable('MULTIPLE_BLOCKS')) {
            params.block = validBlocks;
          } else {
            if (validBlocks.length > 1) {
              logError(
                'You can not use multiple blocks',
                `only rendering the first block : ${validBlocks[0]}`
              );
            }
            params.block = [validBlocks[0]];
          }
        }
        break;
      }
      case 'tab': {
        // Filter only allowed tabs
        const tabParams = value.split(',');
        const validTabs = tabParams.filter(isTab);
        const invalidTabs = tabParams.filter(block => !isTab(block));

        if (invalidTabs.length) {
          logError('Invalid tab parameter', invalidTabs.join(', '));
        }

        if (validTabs) {
          params.tab = validTabs;
        }
        break;
      }
      case 'lang': {
        const lang = value;
        params.lang = lang;
        break;
      }
      case 'scoreboard': {
        if (isScoreboardType(value)) {
          params[key] = value;
        }
        break;
      }
      // default false values
      case 'full_height':
      case 'period_edit':
      case 'multi_lang':
        if (value === 'true') {
          params[key] = true;
        }
        break;
      // default true values
      case 'title':
        if (value === 'false') {
          params[key] = false;
        }
        break;
      case 'period': {
        if (isPeriod(value)) {
          params.period = value;
        } else {
          logError('Invalid period parameter', value);
        }
        break;
      }

      case 'parents': {
        const allParents = value.split(',');
        params.parents = allParents;
        break;
      }

      default:
        params[
          key as keyof Omit<
            UrlParams,
            | 'block'
            | 'tab'
            | 'entity'
            | 'teams'
            | 'lang'
            | 'period_edit'
            | 'period'
            | 'full_height'
            | 'multi_lang'
            | 'title'
            | 'scoreboard'
            | 'parents'
          >
        ] = value;
    }
    return params;
  }, defaultParams);

  if (configContextState.tab.length > 0 && configContextState.block.length > 0) {
    logError('You can not use both tab and block parameters', 'using tab param only');
    configContextState.block = [];
  }

  if (configContextState.id === undefined) {
    logError('Missing required URL parameter', 'id');
  }

  configContextState.entity = entity;
  if (entity !== null && configContextState.block.length === 0) {
    configContextState.block = ENTITY_BLOCKS_CONFIG[entity].full_widget;
  }

  document.documentElement.lang = configContextState.lang;
  i18next.changeLanguage(configContextState.lang);

  if (customer) {
    configContextState.customer = customer;
  }

  // Set data for sentry bug tracking
  setSentryScope(configContextState);

  return <ConfigContext.Provider value={configContextState}>{children}</ConfigContext.Provider>;
};

ConfigContextProvider.displayName = 'ConfigContextProvider';
ConfigContext.displayName = 'ConfigContext';

export default ConfigContext;
export { ConfigContextProvider };
export type { ConfigContextState };
